import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Grid,
  Card,
  Box,
  Autocomplete,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "../../api";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import AWS from "aws-sdk";
import { useDropzone } from "react-dropzone";
import AssigneeHistory from "examples/ReuseFunctions/AssigneeHistory";
import StatusHistory from "examples/ReuseFunctions/StatusHistory";
import { useSelector } from "react-redux";
import StatusTracker from "examples/ReuseFunctions/StatusTracker";
import { RootState } from "store";
import DateFormat from "examples/ReuseFunctions/DateFormat";

interface AssetType {
  asset_type_id: string;
  asset_type_name: string;
  region_id?: string;
}

interface Asset {
  asset_id: string;
  asset_name: string;
}

interface Category {
  category_id: string;
  name: string;
}

interface Employee {
  employee_id: string;
  name: string;
  phone?: string;
}

interface TicketStatus {
  status_id: number;
  status_name: string;
}

interface MultimediaItem {
  multimedia_id: string;
  file_name: string;
  file_type: string;
  file_path: string;
}

interface TicketResponseData {
  category_id: string;
  asset_type_id: string;
  asset_id: string;
  description: string;
  customer_id: string;
  assigned_employee_id: string | null;
  file_name: string | null;
  file_type: string | null;
  status_id: number | null;
  pending_reason: string | null;
  priority_rank: string | null;
  title: string;
  urgency: string;
  category_name: string;
  asset_type_name: string;
  asset_name: string;
  status_name: string;
  employee_name: string;
  region_id?: string;
  multimedia: MultimediaItem[];
  assignee_history?: any;
  status_history?: any;
  status_tracker?: any;
}

interface FormValues {
  category_id: string;
  asset_type_id: string;
  asset_id: string;
  description: string;
  customer_id: string;
  assigned_employee_id: string | null;
  file_name: string | null;
  file_type: string | null;
  status_id: number | null;
  pending_reason: string | null;
  priority_rank: string | null;
  title: string;
  urgency: string;
}

interface FileWithPreview extends File {
  preview: string;
}

interface MediaData {
  file_name: string;
  file_type: string;
  file_path: string;
}

const validationSchema = Yup.object({
  category_id: Yup.string().required("Category is required"),
  asset_type_id: Yup.string().required("Asset Type is required"),
  asset_id: Yup.string().required("Asset is required"),
  description: Yup.string().required("Description is required"),
  title: Yup.string().required("Title is required"),
  urgency: Yup.string(),
});

const conditionValidationSchema = Yup.object({
  category_id: Yup.string().required("Category is required"),
  asset_type_id: Yup.string().required("Asset Type is required"),
  asset_id: Yup.string().required("Asset is required"),
  description: Yup.string().required("Description is required"),
  priority_rank: Yup.string().required("Priority rank is required"),
});

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});

// Extracted helper function to reduce nesting
const uploadFileToS3 = (file: FileWithPreview): Promise<MediaData> => {
  const fileName = `${Date.now()}-${file.name}`;
  const type =
    file.type.split("/")[0] === "image" ? "Photo" : file.type.split("/")[0];

  const params: AWS.S3.PutObjectRequest = {
    Bucket: process.env.REACT_APP_AWS_BUCKET_NAME ?? "",
    Key: fileName,
    Body: file,
    ContentType: file.type,
    ACL: "public-read",
  };

  return new Promise<MediaData>((resolve, reject) => {
    s3.upload(params, (err) => {
      if (err) {
        reject(new Error(`Failed to upload file: ${err.message}`));
        return;
      }
      resolve({
        file_name: fileName,
        file_type: type,
        file_path: fileName,
      });
    });
  });
};

export interface AdminEditTicketProps {
  id?: string;
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const AdminEditTicket: React.FC<AdminEditTicketProps> = ({
  id: idProp,
  embedded,
  onSuccess,
  onCancel,
} = {}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [assetData, setAssetData] = useState<AssetType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [ticketStatuses, setTicketStatuses] = useState<TicketStatus[] | null>(
    null
  );
  const [ticketResponseData, setTicketResponseData] =
    useState<TicketResponseData | null>(null);
  const [initialCategory, setInitialCategory] = useState<Category | null>(null);
  const [initialAssetType, setInitialAssetType] = useState<AssetType | null>(
    null
  );
  const [initialAsset, setInitialAsset] = useState<Asset | null>(null);
  const [initialStatus, setInitialStatus] = useState<TicketStatus | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { fetchError, fetchSuccess } = useSnackbar();

  const navigate = useNavigate();
  const { id: idFromParams } = useParams<{ id: string }>();
  const id = idProp ?? idFromParams;

  const handleDelete = (preview: string): void => {
    setUploadedFiles((prevFiles) => {
      const fileToRemove = prevFiles.find((f) => f.preview === preview);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prevFiles.filter((f) => f.preview !== preview);
    });
  };

  const fetchData = useCallback(async () => {
    try {
      const [assetTypeResponse, ticketResponse, ticketStatuses] =
        await Promise.all([
          axios.get("/asset-types", { params: { is_active: 1 } }),
          axios.get(`/tickets/${id}`),
          axios.get("/ticket-statuses"),
        ]);
      setAssetData(assetTypeResponse.data.list);
      setTicketStatuses(
        ticketStatuses.data.filter(
          (option: TicketStatus) =>
            option.status_id !== 1 && option.status_id !== 2
        )
      );
      const { data } = ticketResponse;
      const ticketData: TicketResponseData = data?.list;

      setInitialStatus({
        status_id: ticketData.status_id ?? 0,
        status_name: ticketData?.status_name,
      });
      setTicketResponseData(ticketData);
      if (ticketData?.assigned_employee_id && ticketData?.employee_name) {
        setSelectedEmployee({
          employee_id: ticketData?.assigned_employee_id ?? "",
          name: ticketData.employee_name,
        });
      }
      setInitialCategory({
        category_id: ticketData.category_id ?? "",
        name: ticketData?.category_name,
      });
      setInitialAssetType({
        asset_type_id: ticketData.asset_type_id ?? "",
        asset_type_name: ticketData?.asset_type_name,
        region_id: ticketData?.region_id,
      });
      setInitialAsset({
        asset_id: ticketData.asset_id ?? "",
        asset_name: ticketData?.asset_name,
      });
    } catch (err) {
      console.error("Error fetching data", err);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchEmployeesData = async () => {
      try {
        const employeeslist = await axios.get(`/employee/roles/4`);
        const { data } = employeeslist;
        setEmployees(data?.list ?? []);
      } catch (err) {
        console.error("Error fetching employees data:", err);
        setEmployees([]);
      }
    };

    fetchEmployeesData();
  }, []);

  const fetchAssetData = useCallback(async () => {
    try {
      const response = await axios.get(
        `/assets/${initialAssetType?.asset_type_id}/${ticketResponseData?.customer_id}`
      );
      setAssets(response?.data?.list);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, [initialAssetType, ticketResponseData]);

  useEffect(() => {
    if (initialAssetType && ticketResponseData) {
      fetchAssetData();
    }
  }, [fetchAssetData, initialAssetType, ticketResponseData]);

  const handleMediaDelete = async (mediaId: string): Promise<void> => {
    try {
      const response = await axios.delete(`/tickets/media/${mediaId}`);
      fetchData();
      fetchSuccess(response.data.message);
    } catch (error: any) {
      fetchError(error.response.data.error);
    }
  };
  const currentTime = DateFormat();

  const [uploadedFiles, setUploadedFiles] = useState<FileWithPreview[]>([]);
  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles: File[]) => {
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ]);
    },
  });

  useEffect(() => {
    const fetchCategoriesData = async () => {
      try {
        const categoriesResponse = await axios.get(
          `/category/asset-type/${initialAssetType?.asset_type_id}`,
          {
            params: {
              is_active: 1,
            },
          }
        );
        setCategories(categoriesResponse.data.list);
      } catch (err) {
        console.error("Error fetching data", err);
      }
    };
    if (initialAssetType) {
      fetchCategoriesData();
    }
  }, [initialAssetType]);

  const priorityRanks: string[] = ["High", "Medium", "Low"];

  const Wrapper: React.ElementType = embedded ? Box : Card;
  const wrapperProps = embedded
    ? { sx: { width: "100%", pt: 0, px: 0 } as any }
    : {
        style: {
          padding: "24px",
          width: "100%",
          borderRadius: "8px",
        },
      };

  const formContent = (
    <Wrapper {...wrapperProps}>
      <SoftTypography variant="h5" sx={{ marginBottom: 2 }}>
        Edit Ticket
      </SoftTypography>
      <Formik
          enableReinitialize
          initialValues={{
            category_id: ticketResponseData?.category_id ?? "",
            asset_type_id: ticketResponseData?.asset_type_id ?? "",
            asset_id: ticketResponseData?.asset_id ?? "",
            description: ticketResponseData?.description ?? "",
            customer_id: ticketResponseData?.customer_id ?? "",
            assigned_employee_id:
              ticketResponseData?.assigned_employee_id ?? null,
            file_name: ticketResponseData?.file_name ?? null,
            file_type: ticketResponseData?.file_type ?? null,
            status_id: ticketResponseData?.status_id ?? null,
            pending_reason: ticketResponseData?.pending_reason ?? null,
            priority_rank: ticketResponseData?.priority_rank ?? null,
            title: ticketResponseData?.title ?? "",
            urgency: ticketResponseData?.urgency ?? "",
          }}
          validationSchema={
            selectedEmployee ? conditionValidationSchema : validationSchema
          }
          onSubmit={async (
            values: FormValues,
            { setSubmitting: formikSetSubmitting }: FormikHelpers<FormValues>
          ) => {
            setSubmitting(true);
            setLoading(true);

            const getStatusMessage = (status_id: number | null | undefined) => {
              switch (status_id) {
                case 2:
                  return "Employee Assigned";
                case 3:
                  return "Work started";
                case 4:
                  return "Ticket is Completed";
                default:
                  return "Employee moved the Ticket";
              }
            };

            // Helper function to handle assignee history
            const handleAssigneeHistoryUpdate = (data: any) => {
              if (
                ticketResponseData?.assigned_employee_id !==
                values.assigned_employee_id
              ) {
                data.assignee_history = AssigneeHistory(
                  ticketResponseData?.assignee_history,
                  selectedEmployee?.name ?? ""
                );
              }
            };

            // Helper function to handle status updates
            const handleStatusUpdate = (data: any) => {
              if (ticketResponseData?.status_id !== values.status_id) {
                data.status_history = StatusHistory(
                  ticketResponseData?.status_history,
                  user?.name ?? "",
                  initialStatus?.status_name ?? ""
                );

                data.status_tracker = StatusTracker({
                  previousData: ticketResponseData?.status_tracker ?? null,
                  message: getStatusMessage(values.status_id),
                  statusName: initialStatus?.status_name ?? "",
                  status_id: initialStatus?.status_id ?? 1,
                  createdby: user?.name ?? "",
                  employeeName: selectedEmployee?.name ?? "",
                  employeePhone: selectedEmployee?.phone ?? "",
                  arraivalDate: currentTime,
                });
              }
            };

            const buildTicketData = () => {
              const data: any = {
                category_id: values.category_id,
                asset_type_id: values.asset_type_id,
                asset_id: values.asset_id,
                description: values.description,
                customer_id: values.customer_id,
                assigned_employee_id: selectedEmployee?.employee_id ?? null,
                status_id: values.status_id ?? "",
                pending_reason: values.pending_reason ?? null,
                priority_rank: values.priority_rank ?? null,
                title: values.title ?? "",
                urgency: values.urgency ?? "",
              };

              // Remove status_id if unchanged
              if (values.status_id === ticketResponseData?.status_id) {
                delete data.status_id;
              }

              // Handle assignee history update
              handleAssigneeHistoryUpdate(data);

              // Handle status history and tracker update
              handleStatusUpdate(data);

              // Reset employee arrival date for status 2
              if (values.status_id === 2) {
                data.employee_arrival_date = null;
              }

              return data;
            };

            const uploadFiles = async () => {
              if (!uploadedFiles?.length) return [];
              return Promise.all(uploadedFiles.map(uploadFileToS3));
            };

            const ticketData = buildTicketData();
            let updatedMediaData: MediaData[] = [];

            try {
              updatedMediaData = await uploadFiles();

              const payload = updatedMediaData.length
                ? { ticketData, mediaData: updatedMediaData }
                : { ticketData };

              const response = await axios.put(`/tickets/${id}`, payload);
              fetchSuccess(response.data.message);
              if (embedded && onSuccess) onSuccess();
              else navigate("/admin/tickets-list");
            } catch (error: any) {
              fetchError(error.response?.data?.error ?? "Something went wrong");
            } finally {
              setSubmitting(false);
              setLoading(false);
            }
          }}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form
              className="edit-ticket-form"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Title
                    </SoftTypography>
                    <Field
                      name="title"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      error={!!errors.title && touched.title}
                      helperText={touched.title && errors.title}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Asset Type
                    </SoftTypography>

                    <Autocomplete
                      options={assetData ?? []}
                      getOptionLabel={(option) => option?.asset_type_name}
                      value={values.asset_type_id ? initialAssetType : null}
                      onChange={(event, value) => {
                        setFieldValue(
                          "asset_type_id",
                          value?.asset_type_id ?? ""
                        );
                        setInitialAssetType(value);
                        setInitialAsset(null);
                        setInitialCategory(null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={
                            !!errors.asset_type_id && !!touched.asset_type_id
                          }
                          helperText={
                            touched.asset_type_id && errors.asset_type_id
                          }
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Brand Name
                    </SoftTypography>
                    <Autocomplete
                      options={assets ?? []}
                      getOptionLabel={(option) => option.asset_name}
                      value={initialAsset}
                      onChange={(event, value) => {
                        setFieldValue("asset_id", value?.asset_id ?? "");
                        setInitialAsset(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.asset_id && touched.asset_id}
                          helperText={touched.asset_id && errors.asset_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Category
                    </SoftTypography>
                    <Autocomplete
                      options={categories ?? []}
                      getOptionLabel={(option) => option.name}
                      value={initialCategory}
                      onChange={(event, value) => {
                        setFieldValue("category_id", value?.category_id ?? "");
                        setInitialCategory(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.category_id && !!touched.category_id}
                          helperText={touched.category_id && errors.category_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Description
                    </SoftTypography>
                    <Field
                      name="description"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      error={!!errors.description && touched.description}
                      helperText={touched.description && errors.description}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Assignee
                    </SoftTypography>
                    <Autocomplete
                      options={employees ?? []}
                      getOptionLabel={(option) => option.name}
                      value={
                        values?.assigned_employee_id ? selectedEmployee : null
                      }
                      onChange={(event, value) => {
                        setFieldValue(
                          "assigned_employee_id",
                          value?.employee_id ?? null
                        );
                        setSelectedEmployee(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={
                            !!errors.assigned_employee_id &&
                            !!touched.assigned_employee_id
                          }
                          helperText={
                            touched.assigned_employee_id &&
                            errors.assigned_employee_id
                          }
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Status
                    </SoftTypography>
                    <Autocomplete
                      options={ticketStatuses ?? []}
                      getOptionLabel={(option) => {
                        if (option.status_id !== 1 && option.status_id !== 2) {
                          return option.status_name;
                        } else {
                          return values.status_id === 2 ? "ToDo" : "Open";
                        }
                      }}
                      value={values.status_id ? initialStatus : null}
                      onChange={(event, value) => {
                        setFieldValue("status_id", value?.status_id ?? "");
                        setInitialStatus(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.status_id && touched.status_id}
                          helperText={touched.status_id && errors.status_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                {values?.assigned_employee_id ? (
                  <Grid item xs={6} sm={4}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Priority Rank
                      </SoftTypography>
                      <Autocomplete
                        options={priorityRanks ?? []}
                        getOptionLabel={(option) => option}
                        value={values.priority_rank}
                        onChange={(event, value) => {
                          setFieldValue("priority_rank", value ?? null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={
                              !!errors.priority_rank && !!touched.priority_rank
                            }
                            helperText={
                              touched.priority_rank && errors.priority_rank
                            }
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                ) : null}
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Urgency Level (1-10)
                    </SoftTypography>
                    <Autocomplete
                      options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                      getOptionLabel={(option) => option.toString()}
                      value={values.urgency ? Number(values.urgency) : null}
                      onChange={(event, value) => {
                        setFieldValue("urgency", value ?? "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.urgency && touched.urgency}
                          helperText={touched.urgency && errors.urgency}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} md={12}>
                  <div
                    className={
                      (ticketResponseData?.multimedia.length ?? 0) > 0 ||
                      uploadedFiles?.length > 0
                        ? "upload-media"
                        : ""
                    }
                  >
                    {ticketResponseData?.multimedia && (
                      <div>
                        {ticketResponseData.multimedia.map((media, index) => (
                          <div
                            className="media-container"
                            key={`${media.file_name}-${media.file_type}`}
                            style={{ position: "relative" }}
                          >
                            {media.file_type === "Photo" ? (
                              <img
                                className="upload-img"
                                src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                                alt={`media-${index}`}
                              />
                            ) : (
                              <video
                                className="upload-video"
                                controls
                                width="100%"
                                height="140px"
                              >
                                <source
                                  src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                                  type="video/mp4"
                                />
                                <track
                                  kind="captions"
                                  src=""
                                  label="No captions available"
                                  default
                                />
                              </video>
                            )}
                            <IconButton
                              onClick={() =>
                                handleMediaDelete(media?.multimedia_id)
                              }
                              style={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                              }}
                              color="secondary"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        ))}
                      </div>
                    )}
                    {uploadedFiles.map((file, index) => (
                      <div
                        className="media-container"
                        key={file.preview}
                        style={{ position: "relative" }}
                      >
                        {file.preview &&
                          (file.type.startsWith("image/") ? (
                            <img src={file.preview} alt={`media-${index}`} />
                          ) : (
                            <video controls width="100%" height="140px">
                              <source src={file.preview} type="video/mp4" />
                              <track
                                kind="captions"
                                src=""
                                label="No captions available"
                                default
                              />
                            </video>
                          ))}
                        <IconButton
                          onClick={() => handleDelete(file.preview)}
                          style={{ position: "absolute", top: 5, right: 5 }}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                  <Box
                    sx={{ cursor: "pointer", width: "fit-content" }}
                    {...dropzone.getRootProps()}
                  >
                    <Box>
                      <label htmlFor="image-upload-input">
                        <input
                          {...dropzone.getInputProps({
                            name: "file_name",
                          })}
                        />
                        <Button
                          className="upload-btn"
                          component="span"
                          variant="contained"
                          color="primary"
                        >
                          Add/Upload File
                        </Button>
                      </label>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Button
                    className="success-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                    }}
                    variant="contained"
                    type="submit"
                    disabled={submitting || loading}
                    color="success"
                  >
                    {loading ? <CircularProgress size={20} /> : "Update"}
                  </Button>

                  <Button
                    className="cancel-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                      ml: 2.5,
                    }}
                    variant="contained"
                    color="warning"
                    onClick={() =>
                      embedded && onCancel
                        ? onCancel()
                        : navigate("/admin/tickets-list")
                    }
                    type="button"
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Form>
          )}
        </Formik>
    </Wrapper>
  );

  if (embedded) return formContent;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {formContent}
    </DashboardLayout>
  );
};

export default AdminEditTicket;
